import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarDays, Wallet, MessageSquare, CheckCheck, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { fmtDate, fmtTimeRange } from "@/lib/format";

function ActionCard({ icon: Icon, label, hint, count, onClick, testid, loading }) {
  const hot = count > 0;
  return (
    <button onClick={onClick} data-testid={testid} title={hint}
      className={`eyw-card p-5 text-left flex items-start gap-3 transition-transform duration-200 hover:-translate-y-0.5 ${hot ? "border-l-4 border-l-[#D98E73]" : ""}`}>
      <Icon size={20} className={hot ? "text-[#D98E73]" : "text-[#8FA89B]"} />
      <div className="min-w-0">
        {loading ? <span className="block h-8 w-10 rounded-md bg-[#E5E0D8] animate-pulse" /> : <p className="text-2xl font-serif text-[#1A242B]" data-testid={`${testid}-count`}>{count}</p>}
        <p className="text-sm text-[#4A5D6B]">{label}</p>
      </div>
    </button>
  );
}

// Role home: upcoming items, outstanding actions and quick links. `go` maps action keys to navigation.
export function useRoleHome(endpoint, queryKey) {
  return useQuery({ queryKey, queryFn: () => api.get(endpoint).then((r) => r.data), refetchInterval: 60000 });
}

export default function RoleHome({ endpoint, queryKey, title, go, quickLinks = [], showUpcoming = true, children }) {
  const { data, isLoading } = useRoleHome(endpoint, queryKey);
  const a = data?.actions || {};
  const items = [...(data?.upcoming || []), ...(data?.events || [])].sort((x, y) => `${x.date}${x.start_time}`.localeCompare(`${y.date}${y.start_time}`)).slice(0, 8);
  return (
    <section className="mb-10 space-y-6" data-testid="role-home">
      <div>
        <h2 className="font-serif text-3xl text-[#1A242B]">{title}</h2>
        <p className="text-sm text-[#4A5D6B] mt-1">What's coming up, what needs you, and shortcuts to what you use most.</p>
      </div>
      {children}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="home-actions">
        <ActionCard icon={Wallet} label="Unpaid balances" hint="Open bookings that still have money owed." count={a.unpaid || 0} onClick={() => go("unpaid")} testid="home-action-unpaid" loading={isLoading} />
        <ActionCard icon={MessageSquare} label="Unanswered messages" hint="New messages and inquiries waiting for a reply." count={a.unread || 0} onClick={() => go("unread")} testid="home-action-unread" loading={isLoading} />
        <ActionCard icon={CheckCheck} label="Pending approvals" hint="Requests waiting for you to accept or decline." count={a.approvals || 0} onClick={() => go("approvals")} testid="home-action-approvals" loading={isLoading} />
      </div>
      <div className={showUpcoming ? "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6" : ""}>
        {showUpcoming && <div className="eyw-card p-6 min-w-0" data-testid="home-upcoming">
          <h3 className="font-serif text-xl text-[#1A242B] mb-3 flex items-center gap-2"><CalendarDays size={18} className="text-[#144F75]" /> Coming up</h3>
          {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : items.length === 0 ? (
            <p className="text-sm text-[#4A5D6B]" data-testid="home-upcoming-empty">Nothing scheduled yet.</p>
          ) : (
            <ul className="divide-y divide-[#E5E0D8]">
              {items.map((it) => (
                <li key={`${it.kind}-${it.id}`} className="py-2.5 flex items-center gap-3" data-testid={`home-upcoming-${it.kind}-${it.id}`}>
                  <span className="text-xs w-24 shrink-0 text-[#8FA89B]">{fmtDate(it.date)}<br />{it.start_time ? fmtTimeRange(it.start_time) : ""}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#1A242B] truncate">{it.title}</p>
                    <p className="text-xs text-[#4A5D6B] truncate">{it.kind === "event" ? "Event" : [it.who, it.enhancer].filter(Boolean).join(" with ")}</p>
                  </div>
                  {it.kind === "event" ? <Link to={`/events/${it.slug || it.id}`} className="text-xs text-[#144F75] hover:underline">Open</Link>
                    : it.payment_status === "unpaid" && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#B5794E]">unpaid</span>}
                </li>
              ))}
            </ul>
          )}
        </div>}
        <div className="eyw-card p-6" data-testid="home-quick-links">
          <h3 className="font-serif text-xl text-[#1A242B] mb-3">Quick links</h3>
          <ul className={showUpcoming ? "space-y-1" : "grid sm:grid-cols-2 lg:grid-cols-4 gap-1"}>
            {quickLinks.map((q) => (
              <li key={q.label}>
                <button onClick={q.onClick} data-testid={`home-quick-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group w-full flex items-center justify-between rounded-xl px-3 py-2 text-sm text-[#1A242B] hover:bg-[#144F75]/[0.06] transition-colors">
                  {q.label} <ArrowRight size={14} className="text-[#144F75] transition-transform group-hover:translate-x-0.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
